/**
 * Scope-filter model, shared by every filter language (#1867 PR2).
 *
 * The visual builder holds a filter as conditions (field, operator, values)
 * joined by match ALL or ANY, or as raw text in the Advanced view. That model
 * says nothing about any tracker's query language. Turning it into a
 * language and back is a translator's job: each one lives in its own file
 * under `scopeFilter/` and is registered here by the language name the
 * tracker's descriptor gives (`filterLanguage`). A tracker whose language has
 * no translator gets a plain text box.
 *
 * Contract every translator keeps (and is tested against):
 *   - `compile` runs ONLY on builder conditions, never on an Advanced string.
 *   - `parse` recognizes exactly the grammar `compile` emits (plus a few
 *     lenient hand-edits); ANYTHING it can't model returns
 *     `{ mode: 'advanced', text }` with the ORIGINAL string byte-preserved.
 *     The card then renders that raw string and never re-compiles it, so
 *     there is no lossy round-trip.
 *   - Both handle the filter FRAGMENT only. The server owns the project and
 *     type clauses and the ordering, and wraps this fragment in parentheses.
 */
import { jqlTranslator } from './scopeFilter/jql'
import { githubSearchTranslator } from './scopeFilter/githubSearch'

export type MatchMode = 'all' | 'any' // all → AND, any → OR

/** Operators. Set operators (`isAnyOf`/`isNotAnyOf`/`isEmpty`) drive the
 * string-set fields; the comparison operators (`inLast`/`before`/`after`)
 * drive the date fields (#1867 PR2b). `isEmpty`/`inLast` take no free values
 * beyond, respectively, none and the single relative token. `is` takes one
 * value from a fixed list (GitHub's State and Closed as). */
export type OperatorKey = 'isAnyOf' | 'isNotAnyOf' | 'isEmpty' | 'inLast' | 'before' | 'after' | 'is'

/** Where a field's values come from, which picks the builder's value editor:
 * a fixed list on the field (`fixed`), the tracker's versions or priorities,
 * label type-ahead, people search, or a date. */
export type ValueKind = 'fixed' | 'versions' | 'priorities' | 'labels' | 'user' | 'date'

export interface Condition {
  /** A field key from the translator's field list. */
  field: string
  op: OperatorKey
  /** The condition's value(s). Uniform `string[]` across field types:
   *   - set fields hold the human NAMES (workflow stage / version / priority /
   *     label), or, for people, the opaque `accountId`s;
   *   - date fields hold a SINGLE token in `values[0]` — a relative
   *     `-Nd`/`-Nw` (for `inLast`) or an ISO date `yyyy-MM-dd` (before/after).
   * Empty for `isEmpty`, or while a fresh condition is still incomplete. */
  values: string[]
}

export interface BuilderModel {
  mode: 'builder'
  match: MatchMode
  conditions: Condition[]
}
export interface AdvancedModel {
  mode: 'advanced'
  /** The raw filter text in the tracker's language, preserved verbatim. */
  text: string
}
export type ScopeFilterModel = BuilderModel | AdvancedModel

export interface FieldConfig {
  key: string
  label: string
  valueKind: ValueKind
  /** Operators this field offers — set operators for string-set fields,
   * comparison operators for date fields (#1867 PR2b). */
  operators: OperatorKey[]
  /** The values a `fixed` field offers. */
  options?: readonly string[]
  /** Wording for an operator on this field when it differs from the usual
   * ("is none of", "has none"). */
  operatorLabels?: Partial<Record<OperatorKey, string>>
  /** The add-a-value prompt for a listed field ("+ milestone"). */
  addPlaceholder?: string
}

/** One filter language: its builder fields, and the two directions between
 * the model and the language's text. */
export interface ScopeFilterTranslator {
  /** The language's name as the Advanced view shows it, e.g. "JQL". */
  languageName: string
  /** Field list for the builder's field dropdown, in display order. The
   * first is the one a new condition starts on. */
  fields: ReadonlyArray<FieldConfig>
  compile(model: ScopeFilterModel): string
  parse(text: string | undefined | null): ScopeFilterModel
  /** Whether saved label and version names are checked against the
   * selected projects when the filter opens, for "not found" marks. Only a
   * tracker whose values belong to each project (GitHub) has a check. */
  checksSavedValues?: boolean
  /** The label and version names written in a filter's text, for checking
   * a filter shown in the Advanced view. Reads the text; never rewrites it. */
  savedValueNames?(text: string): { labels: string[]; versions: string[] }
  /** A saved person value is already readable (GitHub writes the login), so
   * it is shown as is when no name was looked up. Absent: the value is an
   * opaque id (Jira's account id) and shows as "Unknown user". */
  peopleValuesReadable?: boolean
}

/** Translators by the language name a tracker's descriptor gives. */
const TRANSLATORS: Record<string, ScopeFilterTranslator> = {
  jql: jqlTranslator,
  'github-search': githubSearchTranslator,
}

/** The translator for a filter language, or undefined when there is none
 * (the screen then shows a plain text box). */
export function getScopeFilterTranslator(language: string | undefined): ScopeFilterTranslator | undefined {
  return language ? TRANSLATORS[language] : undefined
}

const OPERATOR_LABELS: Record<OperatorKey, string> = {
  isAnyOf: 'is any of',
  isNotAnyOf: 'is not any of',
  isEmpty: 'is empty',
  inLast: 'in the last',
  before: 'before',
  after: 'after',
  is: 'is',
}

/** A field's config from a translator's list. */
export function fieldConfig(translator: ScopeFilterTranslator, key: string): FieldConfig | undefined {
  return translator.fields.find((f) => f.key === key)
}

/** The operators a field offers, with UI labels. `isEmpty` reads "is
 * unassigned" for people fields, "is empty" elsewhere. */
export function operatorsForField(cfg: FieldConfig | undefined): ReadonlyArray<{ key: OperatorKey; label: string }> {
  if (!cfg) return []
  return cfg.operators.map((k) => ({
    key: k,
    label: cfg.operatorLabels?.[k] ?? (k === 'isEmpty' && cfg.valueKind === 'user' ? 'is unassigned' : OPERATOR_LABELS[k]),
  }))
}

/** A condition is ready to compile/save when it needs no values (`isEmpty`)
 * or has at least one non-blank value. */
export function isConditionComplete(c: Condition): boolean {
  return c.op === 'isEmpty' || c.values.some((v) => v.trim().length > 0)
}
